import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, LabelList
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Styled components
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const UniqueSuppliersByRegionChart = ({ purchaseData = [], clientsData = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [regionStats, setRegionStats] = useState([]);

  useEffect(() => {
    if (!purchaseData.length) return;
    if (!selectedYear) {
      const latestYear = Math.max(...purchaseData.map(r => parseInt(r.Rok)));
      setSelectedYear(latestYear);
    }
  }, [purchaseData, selectedYear]);

  useEffect(() => {
    if (!selectedYear) return;

    const includeFullYear = selectedMonth === 0;

    const filtered = purchaseData.filter(row => {
      const rok = parseInt(row.Rok);
      const miesiac = parseInt(row.Miesiac);
      return rok === selectedYear && (includeFullYear || miesiac === selectedMonth);
    });

    const activeNIPs = new Set(
      filtered.map(row => row.NrDostawcy?.trim()).filter(Boolean)
    );

    const regionMap = {};
    const unknownNIPs = [];

    clientsData.forEach(client => {
      const nip = client.nip?.trim();
      const region = client.wojewodztwo?.trim();
      const isActive = activeNIPs.has(nip);

      if (!nip || !isActive) return;

      if (
        !region ||
        region.trim() === "" ||
        region.toLowerCase() === "nieznany" ||
        region.toLowerCase() === "brak danych"
      ) {
        unknownNIPs.push({ nip, ...client });
        return;
      }

      if (!regionMap[region]) {
        regionMap[region] = new Set();
      }
      regionMap[region].add(nip);
    });

    const data = Object.entries(regionMap)
      .map(([region, nipSet]) => ({
        region,
        count: nipSet.size,
      }))
      .sort((a, b) => b.count - a.count);

    setRegionStats(data);

    if (unknownNIPs.length) {
      console.log("📌 Dostawcy bez przypisanego województwa:", unknownNIPs);
    } else {
      console.log("✅ Wszyscy dostawcy mają przypisane województwa");
    }
  }, [purchaseData, clientsData, selectedYear, selectedMonth]);

  const getMonthName = (m) =>
    new Date(2000, m - 1).toLocaleString("pl-PL", { month: "long" });

  const availableYears = [...new Set(purchaseData.map(r => parseInt(r.Rok)))].sort((a, b) => b - a);
  const availableMonths = selectedYear
    ? [...new Set(
        purchaseData.filter(r => parseInt(r.Rok) === selectedYear).map(r => parseInt(r.Miesiac))
      )].sort((a, b) => a - b)
    : [];

  return (
    <Wrapper>
      <ChartTitle>
        Liczba unikalnych dostawców wg województw
        <InfoIconWrapper data-tooltip-id="unique-suppliers-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="unique-suppliers-tooltip"
        place="left"
        content={
          <>
            <strong>Unikalni dostawcy</strong><br />
            Pokazuje liczbę unikalnych dostawców wg województw,<br />
            którzy brali udział w zakupach w danym okresie.
          </>
        }
      />

      <Controls>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Miesiąc:</label>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
            <option value={0}>Cały rok</option>
            {availableMonths.map(m => (
              <option key={m} value={m}>{getMonthName(m)}</option>
            ))}
          </Select>
        </div>
      </Controls>

      {regionStats.length === 0 ? (
        <p style={{ marginTop: "20px", color: "#888" }}>
          Brak danych do wyświetlenia.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={520}>
          <BarChart
            data={regionStats}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              tickFormatter={(val) => val.toLocaleString("pl-PL")}
            />
            <YAxis type="category" dataKey="region" />
            <Tooltip formatter={(val) => val.toLocaleString("pl-PL")} />
            <Bar dataKey="count" fill="#9848c7">
              <LabelList
                dataKey="count"
                position="right"
                formatter={(val) => val.toLocaleString("pl-PL")}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Wrapper>
  );
};

export default UniqueSuppliersByRegionChart;
